import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Container, Typography, TextField, Button, Paper, Stack, Box, IconButton,
  Snackbar, Alert, Dialog, DialogTitle, DialogContent, CircularProgress,
  Avatar, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  DialogActions, Zoom, Chip, Tooltip, TablePagination
} from "@mui/material";

// Icons
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import LogoutIcon from "@mui/icons-material/Logout";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import GridOnIcon from "@mui/icons-material/GridOn";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import StorageIcon from "@mui/icons-material/Storage";
import RefreshIcon from "@mui/icons-material/Refresh";

import axios from "axios";
import { useNavigate } from "react-router-dom";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";

const BASE_URL = `${process.env.REACT_APP_API_URL}/api/super-admin`;
const TELE_STATUS_URL = `${BASE_URL}/db/status`;
const TELE_FREE_UP_URL = `${BASE_URL}/db/free-up`;

const BenSuperDashboard = () => {
  const [admins, setAdmins] = useState([]);
  const [adminForm, setAdminForm] = useState({ name: "", username: "", password: "" });
  const [editId, setEditId] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Pagination State
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [collections, setCollections] = useState([]);
  const [dbDialogOpen, setDbDialogOpen] = useState(false);
  const [dbLoading, setDbLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const token = localStorage.getItem("token");
  const navigate = useNavigate();

  const showSnackbar = (message, severity = "success") => setSnackbar({ open: true, message, severity });

  const fetchAdmins = useCallback(async (manual = false) => {
    try {
      const res = await axios.get(`${BASE_URL}/admins`, { 
        headers: { Authorization: `Bearer ${token}` } 
      });
      setAdmins(res.data);
      
      if (manual) {
        showSnackbar("Table Updated", "success");
      }
    } catch (err) { 
      showSnackbar("Terminal Sync Failed", "error"); 
    }
  }, [token]);

  // Initial load (silent)
  useEffect(() => { 
    fetchAdmins(false); 
  }, [fetchAdmins]);

  // Debounce search
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(0); // Reset to first page when search changes
    }, 300);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  const fetchDbStatus = async () => {
    try {
      const res = await axios.get(TELE_STATUS_URL, { headers: { Authorization: `Bearer ${token}` } });
      const data = res.data.collections || res.data || [];
      setCollections(data);
      return data;
    } catch (err) {
      showSnackbar("DB Status Sync Failed", "error");
      return [];
    }
  };

  useEffect(() => {
    fetchAdmins();
    fetchDbStatus();
  }, [fetchAdmins]);

  const handleSaveAdmin = async () => {
    try {
      if (editId) {
        await axios.put(`${BASE_URL}/admins/${editId}`, adminForm, { headers: { Authorization: `Bearer ${token}` } });
        showSnackbar("Registry Updated");
      } else {
        await axios.post(`${BASE_URL}/create-admin`, adminForm, { headers: { Authorization: `Bearer ${token}` } });
        showSnackbar("New Access Created");
      }
      setFormOpen(false);
      fetchAdmins();
      setAdminForm({ name: "", username: "", password: "" });
    } catch (err) { 
      showSnackbar("Operation Failed", "error"); 
    }
  };

  const exportToExcel = async () => {
    const currentDb = await fetchDbStatus();
    const wsAdmins = XLSX.utils.json_to_sheet(filteredAdmins.map(a => ({ Name: a.name, Username: a.username, Role: a.role })));
    const wsDb = XLSX.utils.json_to_sheet(currentDb.map(c => ({ Collection: c.name, Document_Count: c.count })));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, wsAdmins, "Admins");
    XLSX.utils.book_append_sheet(wb, wsDb, "Database_Status");
    XLSX.writeFile(wb, "Full_Terminal_Report.xlsx");
    showSnackbar("Excel Export Complete");
  };

  const exportToPDF = async () => {
    const currentDb = await fetchDbStatus();
    const doc = new jsPDF();
    doc.setTextColor(0, 0, 0);
    doc.text("SUPER TERMINAL SYSTEM REPORT", 14, 15);
    autoTable(doc, {
      startY: 25,
      head: [['Full Name', 'Username', 'Role']],
      body: filteredAdmins.map(a => [a.name, a.username, a.role]),
      headStyles: { fillColor: [255, 214, 0] }
    });
    const finalY = doc.lastAutoTable.finalY + 10;
    doc.text("Database Telemetry Status", 14, finalY);
    autoTable(doc, {
      startY: finalY + 5,
      head: [['Collection Name', 'Record Count']],
      body: currentDb.map(c => [c.name, c.count]),
      headStyles: { fillColor: [255, 214, 0] }
    });
    doc.save("Full_Terminal_Report.pdf");
    showSnackbar("PDF Export Complete");
  };

  const handleShowDbStatus = async () => {
    setDbDialogOpen(true);
    setDbLoading(true);
    await fetchDbStatus();
    setDbLoading(false);
  };

  const handleFreeUp = async () => {
    if (!window.confirm("Purge all telemetry data?")) return;
    try {
      await axios.delete(TELE_FREE_UP_URL, { headers: { Authorization: `Bearer ${token}` } });
      showSnackbar("Database Cleared", "info");
      setDbDialogOpen(false);
      fetchDbStatus();
    } catch (err) { 
      showSnackbar("Purge Failed", "error"); 
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Terminate this access?")) return;
    try {
      await axios.delete(`${BASE_URL}/admins/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      fetchAdmins();
      showSnackbar("Access Revoked", "warning");
    } catch (err) { 
      showSnackbar("Delete Failed", "error"); 
    }
  };

  const handleLogout = () => { 
    localStorage.removeItem("token"); 
    navigate("/login"); 
  };

  // Filter and pagination logic
  const filteredAdmins = useMemo(() => {
    return admins.filter(a => 
      a.username.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
      a.name?.toLowerCase().includes(debouncedSearch.toLowerCase())
    );
  }, [admins, debouncedSearch]);

  const paginatedAdmins = useMemo(() => {
    return filteredAdmins.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [filteredAdmins, page, rowsPerPage]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // --- White Theme with Yellow Accents ---
  const whiteCard = {
    backgroundColor: "#ffffff",
    border: "1px solid #e0e0e0",
    borderRadius: "12px",
    color: "#000000",
    boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
  };

  const yellowButton = {
    background: "#FFD600",
    color: "#000000",
    fontWeight: "bold",
    "&:hover": {
      background: "#ffcc00",
      boxShadow: "0 4px 12px rgba(255, 214, 0, 0.5)",
    },
  };

  const whiteInput = {
    "& .MuiOutlinedInput-root": {
      color: "#000000",
      backgroundColor: "#ffffff",
      "& fieldset": { borderColor: "#e0e0e0" },
      "&:hover fieldset": { borderColor: "#FFD600" },
      "&.Mui-focused fieldset": { borderColor: "#FFD600" },
    },
    "& .MuiInputLabel-root": { color: "#666666" },
  };

  const tableHeadCell = { 
    color: "#000000", 
    fontWeight: 800, 
    borderBottom: '2px solid #FFD600',
    backgroundColor: "#f5f5f5"
  };
  const tableCell = { 
    color: "#000000", 
    borderBottom: '1px solid #e0e0e0' 
  };

  return (
    <Box minHeight="100vh" sx={{ backgroundColor: "#f5f5f5", py: 4 }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Paper sx={{ p: 3, mb: 4, ...whiteCard, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box display="flex" alignItems="center" gap={2}>
            <Avatar sx={{ bgcolor: "#FFD600", color: "#000000" }}>
              <StorageIcon />
            </Avatar>
            <Typography variant="h5" fontWeight={900} sx={{ color: "#000000" }}>
              SUPER TERMINAL
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Tooltip title="Export Excel">
              <IconButton onClick={exportToExcel} sx={{ ...yellowButton, background: "transparent", border: "1px solid #FFD600" }}>
                <GridOnIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Export PDF">
              <IconButton onClick={exportToPDF} sx={{ ...yellowButton, background: "transparent", border: "1px solid #FFD600" }}>
                <PictureAsPdfIcon />
              </IconButton>
            </Tooltip>
            <Button
              variant="outlined"
              onClick={handleLogout}
              startIcon={<LogoutIcon />}
              sx={{ 
                borderColor: "#ff4444", 
                color: "#ff4444", 
                "&:hover": { 
                  borderColor: "#ff0000", 
                  bgcolor: "rgba(255, 68, 68, 0.1)" 
                } 
              }}
            >
              Logout
            </Button>
          </Stack>
        </Paper>

        {/* Toolbar */}
        <Paper 
          sx={{ 
            px: 3, 
            mb: 4, 
            ...whiteCard, 
            display: 'flex', 
            alignItems: 'center', 
            height: '70px'
          }}
        >
          <Stack 
            direction="row" 
            justifyContent="space-between" 
            alignItems="center"
            spacing={2} 
            sx={{ width: '100%' }}
          >
            <Stack direction="row" spacing={2}>
              <Button 
                variant="contained" 
                startIcon={<PersonAddIcon />} 
                onClick={() => { 
                  setEditId(null); 
                  setAdminForm({ name: "", username: "", password: "" }); 
                  setFormOpen(true); 
                }} 
                sx={yellowButton}
              >
                Add
              </Button>
              <Button 
                variant="outlined" 
                startIcon={<StorageIcon />} 
                onClick={handleShowDbStatus} 
                sx={{ ...yellowButton, background: "transparent", border: "1px solid #FFD600" }}
              >
                DB
              </Button>
            </Stack>

            <Box display="flex" alignItems="center" gap={1}>
              <TextField 
                placeholder="Search..." 
                size="small" 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)} 
                sx={whiteInput} 
              />
              <IconButton
                onClick={() => fetchAdmins(true)}
                sx={{ ...yellowButton, background: "transparent", border: "1px solid #FFD600" }}
              >
                <RefreshIcon />
              </IconButton>
            </Box>
          </Stack>
        </Paper>

        {/* Table with Scroll Bars and Pagination */}
        <Paper sx={whiteCard}>
          <TableContainer 
            sx={{ 
              maxHeight: "calc(100vh - 350px)",
              overflow: "auto",
              "&::-webkit-scrollbar": {
                width: "8px",
                height: "8px",
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor: "#f5f5f5",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#FFD600",
                borderRadius: "4px",
              },
            }}
          >
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={tableHeadCell}>Full Name</TableCell>
                  <TableCell sx={tableHeadCell}>Username</TableCell>
                  <TableCell sx={tableHeadCell}>Role</TableCell>
                  <TableCell sx={tableHeadCell} align="right">Manage</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedAdmins.map((admin) => (
                  <TableRow key={admin._id}>
                    <TableCell sx={tableCell}>{admin.name || "N/A"}</TableCell>
                    <TableCell sx={tableCell}>{admin.username}</TableCell>
                    <TableCell sx={tableCell}>
                      <Chip 
                        label={admin.role} 
                        size="small" 
                        sx={{ 
                          color: '#000000', 
                          borderColor: '#FFD600',
                          backgroundColor: '#FFF5E0'
                        }} 
                        variant="outlined" 
                      />
                    </TableCell>
                    <TableCell align="right" sx={tableCell}>
                      <IconButton 
                        onClick={() => { 
                          setEditId(admin._id); 
                          setAdminForm({ name: admin.name, username: admin.username, password: "" }); 
                          setFormOpen(true); 
                        }} 
                        sx={{ color: "#FFD600", "&:hover": { bgcolor: "#FFD600", color: "#000000" } }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton 
                        onClick={() => handleDelete(admin._id)} 
                        sx={{ color: "#ff4444", "&:hover": { bgcolor: "#ff0000", color: "#fff" } }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {paginatedAdmins.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} sx={{ textAlign: "center", py: 5, color: "#666666" }}>
                      NO REGISTRY FOUND
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
            component="div"
            count={filteredAdmins.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              color: "#000000",
              borderTop: "1px solid #e0e0e0",
              "& .MuiTablePagination-selectIcon": { color: "#FFD600" },
              "& .MuiIconButton-root": { color: "#FFD600" },
              "& .MuiTablePagination-select": { color: "#000000" },
              "& .MuiTypography-root": { color: "#000000" },
            }}
          />
        </Paper>

        {/* COMPACT DB Popup */}
        <Dialog
          open={dbDialogOpen}
          onClose={() => setDbDialogOpen(false)}
          fullWidth
          maxWidth="xs"
          PaperProps={{ sx: whiteCard }}
        >
          <DialogTitle sx={{ color: '#000000', fontSize: '1.1rem', pb: 1, fontWeight: 'bold' }}>
            DATABASE STATUS
          </DialogTitle>
          <DialogContent sx={{ py: 1 }}>
            {dbLoading ? (
              <Box display="flex" justifyContent="center" py={2}>
                <CircularProgress size={24} sx={{ color: '#FFD600' }} />
              </Box>
            ) : (
              <Stack spacing={1}>
                {collections.map((col, i) => (
                  <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #e0e0e0', py: 0.5 }}>
                    <Typography variant="body2" sx={{ color: '#000000' }}>{col.name}</Typography>
                    <Typography variant="body2" sx={{ color: '#FFD600', fontWeight: 'bold' }}>{col.count}</Typography>
                  </Box>
                ))}
              </Stack>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 1.5 }}>
            <Button size="small" onClick={() => setDbDialogOpen(false)} sx={{ color: '#666666' }}>Close</Button>
            <Button size="small" variant="contained" sx={{ bgcolor: '#ff4444', color: '#fff', '&:hover': { bgcolor: '#ff0000' } }} onClick={handleFreeUp}>Purge</Button>
          </DialogActions>
        </Dialog>

        {/* COMPACT Admin Form Popup */}
        <Dialog
          open={formOpen}
          onClose={() => setFormOpen(false)}
          fullWidth
          maxWidth="xs"
          PaperProps={{ sx: whiteCard }}
        >
          <DialogTitle sx={{ color: '#000000', fontSize: '1.1rem', pb: 1, fontWeight: 'bold' }}>
            {editId ? "MODIFY ACCESS" : "NEW REGISTRATION"}
          </DialogTitle>
          <DialogContent sx={{ py: 1 }}>
            <Stack spacing={2} mt={1}>
              <TextField 
                label="Full Name" 
                size="small" 
                fullWidth 
                value={adminForm.name} 
                onChange={(e) => setAdminForm({ ...adminForm, name: e.target.value })} 
                sx={whiteInput} 
              />
              <TextField 
                label="Username" 
                size="small" 
                fullWidth 
                value={adminForm.username} 
                onChange={(e) => setAdminForm({ ...adminForm, username: e.target.value.toLowerCase() })} 
                sx={whiteInput}
                helperText="Username will be automatically lowercased"
              />
              <TextField 
                label="Password" 
                size="small" 
                type="password" 
                placeholder={editId ? "Keep blank" : ""} 
                fullWidth 
                value={adminForm.password} 
                onChange={(e) => setAdminForm({ ...adminForm, password: e.target.value })} 
                sx={whiteInput} 
              />
            </Stack>
          </DialogContent>
          <DialogActions sx={{ p: 1.5 }}>
            <Button size="small" onClick={() => setFormOpen(false)} sx={{ color: '#666666' }}>Cancel</Button>
            <Button size="small" variant="contained" sx={yellowButton} onClick={handleSaveAdmin}>Confirm</Button>
          </DialogActions>
        </Dialog>

      </Container>
      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar(p => ({ ...p, open: false }))}>
        <Alert severity={snackbar.severity} sx={{ bgcolor: "#ffffff", color: "#000000", border: "1px solid #FFD600" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BenSuperDashboard;